import React from "react";
import { Link } from "react-router-dom";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Table from "../../components/ui/Table";
import Pagination from "../../components/ui/Pagination";
import checkPermissions from "../../global-functions/checkPermissionsFunc";
import { formatCurrency } from "../../../utils/formatCurrency";

export default function OrdersTableSection({
  data = [],
  loading,
  currentPage,
  perPage,
  totalItems,
  onPageChange,
  arrayWhichSelected,
  handlePush,
  handleDelete,
  setShowUpdateModel,
  setOrderWhichUpdate,
}) {
  const { t } = useTranslation();

  const renderStatus = (status) => {
    const map = {
      0: { text: "Pending", color: "badge bg-warning" },
      1: { text: "Active", color: "badge bg-success" },
      2: { text: "Delivery", color: "badge bg-info" },
      3: { text: "Completed", color: "badge bg-primary" },
    };
    const { text, color } = map[status] || map[0];
    return <span className={`${color}`}>{t(text)}</span>;
  };

  const renderPaymentStatus = (status) => {
    const map = {
      complete: { text: "Completed", color: "badge bg-success" },
      pending: { text: "Pending", color: "badge bg-warning" },
      failed: { text: "Failed", color: "badge bg-danger" },
    };
    const { text, color } = map[status] || map.pending;
    return <span className={`${color}`}>{t(text)}</span>;
  };

  const columns = [
    {
      header: (
        <input
          type="checkbox"
          className={`form-check-input ${data?.length <= 0 ? "disabled" : ""}`}
          id="select-all-orders"
          onChange={(e) => {
            if (e.target.checked) {
              const ids = data.map((item) => item.id);
              handlePush(ids);
            } else {
              const ids = data.map((item) => item.id);
              handleDelete(ids);
            }
          }}
          checked={
            data.length > 0 &&
            arrayWhichSelected.length === data.length &&
            data.every((item) => arrayWhichSelected.includes(item.id))
          }
        />
      ),
      render: (order) => (
        <input
          type="checkbox"
          className="form-check-input"
          checked={arrayWhichSelected.includes(order.id)}
          onChange={(e) =>
            e.target.checked ? handlePush(order.id) : handleDelete(order.id)
          }
        />
      ),
    },
    {
      header: t("Invoice ID"),
      render: (order) => (
        <Link to={`/invoice-page/${order.id}`}><strong>#{order.invoice_no}</strong></Link>
      ),
    },
    {
      header: t("User"),
      render: (order) => {
        const displayName = order.user
          ? `${order.user.first_name} ${order.user.last_name}`
          : order.guest_name || t("Guest");
        const email = order.user?.email || order.guest_email;

        if (!email) {
          return displayName;
        }

        return (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`order-user-email-${order.id}`}>{email}</Tooltip>
            }
          >
            <span>{displayName}</span>
          </OverlayTrigger>
        );
      },
    },
    {
      header: t("Status"),
      render: (order) => renderStatus(order.status),
    },
    {
      header: t("Refund status"),
      render: (order) => (
        <span
          className={`badge ${
            order.refund_status == 0 ? "bg-success" : "bg-danger"
          }`}
        >
          {order.refund_status == 0 ? t("No Refund") : t("Refund")}
        </span>
      ),
    },
    {
      header: t("Payment status"),
      render: (order) => renderPaymentStatus(order.payment_status),
    },
    {
      header: t("Total"),
      render: (order) => formatCurrency(parseFloat(order.payable_amount || 0)),
    },
    {
      header: t("Payment gateway"),
      render: (order) => order.payment_gateway?.replace(/_/g, " "),
    },
  ];

  const renderActions = (row) =>
    checkPermissions(
      "update orders",
      <Dropdown>
        <Dropdown.Toggle variant="secondary" className="btn-sm">
          <i className="fas fa-ellipsis-v"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item
            onClick={() => {
              setShowUpdateModel(true);
              setOrderWhichUpdate(row);
            }}
          >
            {t("Update") + " " + t("Order")}
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              window.location.href = `/update-order-redfund/${row.id}`;
            }}
          >
            {t("Update") + " " + t("Refund")}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );

  return (
    <>
      <Table
        data={data}
        loading={loading}
        columns={columns}
        actions={renderActions}
      />
      <Pagination
        totalItems={totalItems}
        currentPage={currentPage - 1}
        perPage={perPage}
        onPageChange={(page) => onPageChange(page + 1)}
        t={t}
      />
    </>
  );
}
